import { Link } from "react-router-dom";
import { BookOpen, Heart, Target, ListChecks, Sun, Flame, Sparkle, ArrowRight, FacebookLogo, InstagramLogo } from "@phosphor-icons/react";

// EDIT THESE TWO LINES with the exact URLs/handles when you have them.
const FACEBOOK_URL = "https://www.facebook.com/intuitiveangelagehl";
const INSTAGRAM_URL = "https://www.instagram.com/its.angelagehl";

// EDIT THESE — paste 3 Instagram post shortcodes (the bit after /p/ in the URL).
// Example URL: https://www.instagram.com/p/CzABC123xyz/  →  shortcode is "CzABC123xyz"
// Leave the array empty [] to hide the section.
const INSTAGRAM_POST_SHORTCODES = ["EXAMPLE1", "EXAMPLE2", "EXAMPLE3"];

const HERO_IMG = "https://images.pexels.com/photos/32041165/pexels-photo-32041165.jpeg";
const JOURNAL_IMG = "https://images.unsplash.com/photo-1562878274-ad7a29ea8cdd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxqb3VybmFsJTIwbm90ZWJvb2slMjBjb2ZmZWUlMjBjYWxtfGVufDB8fHx8MTc4MjI2MzA3Nnww&ixlib=rb-4.1.0&q=85";
const NATURE_IMG = "https://images.pexels.com/photos/4324349/pexels-photo-4324349.jpeg";

const FEATURES = [
  { icon: BookOpen, title: "Guided daily prompts", text: "Thoughtfully crafted questions, gently tuned by AI to meet you where you are." },
  { icon: Heart, title: "Mood tracker", text: "Notice the weather of your inner life with soft, organic charts." },
  { icon: Target, title: "Goals that breathe", text: "Set intentions for the season; track progress without pressure." },
  { icon: ListChecks, title: "Daily tasks", text: "Small commitments that compound into who you're becoming." },
  { icon: Sun, title: "Gratitude log", text: "Three small things a day. A practice that rewires your default lens." },
  { icon: Flame, title: "Habit streaks", text: "Quiet consistency. Honest streaks. No shame for resetting." },
];

export default function Landing() {
  return (
    <div className="bg-sand text-charcoal">
      {/* NAV */}
      <header className="sticky top-0 z-30 glass border-b border-border/40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <span className="w-2.5 h-2.5 rounded-full bg-moss"></span>
            <span className="font-serif text-xl md:text-2xl tracking-tight leading-tight">Rediscover Yourself <span className="text-clay text-sm md:text-base block md:inline">· with Angela Gehl</span></span>
          </Link>
          <nav className="flex items-center gap-2">
            <Link to="/" data-testid="nav-home"
                  className="text-sm px-4 py-2 rounded-full hover:bg-cream transition-colors hidden sm:inline-block">Home</Link>
            <Link to="/login" data-testid="nav-login"
                  className="text-sm px-4 py-2 rounded-full hover:bg-cream transition-colors">Sign in</Link>
            <Link to="/register" data-testid="nav-cta-start"
                  className="text-sm px-5 py-2 rounded-full bg-moss text-white hover:bg-mossDark transition-colors">
              Start free trial
            </Link>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-6">A 90-day inward journey</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif leading-[1.05] font-medium mb-6">
              Meet the quietest, <em className="text-clay not-italic">truest</em><br/>
              version of yourself.
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-xl mb-8 leading-relaxed">
              A gentle 90-day journal for self-discovery, guided by Angela Gehl — daily AI-curated prompts,
              mood tracking, goals, gratitude, and habits, all woven into one quiet space.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/register" data-testid="hero-cta-start"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-moss text-white hover:bg-mossDark transition-all hover:-translate-y-0.5">
                Begin 7-day free trial <ArrowRight size={18} />
              </Link>
              <Link to="/login" data-testid="hero-cta-signin"
                    className="px-6 py-3 rounded-full border border-border hover:bg-cream transition-colors">
                I already have an account
              </Link>
            </div>
            <p className="text-xs text-muted-foreground mt-4">No credit card for trial · $29.99 lifetime after</p>
            <div className="flex items-center gap-3 mt-5">
              <span className="text-xs text-muted-foreground">Follow Angela</span>
              <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" aria-label="Facebook"
                 data-testid="hero-facebook" className="w-9 h-9 rounded-full bg-white/70 border border-border flex items-center justify-center hover:bg-cream hover:-translate-y-0.5 transition-all">
                <FacebookLogo size={16} weight="duotone" className="text-moss" />
              </a>
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Instagram"
                 data-testid="hero-instagram" className="w-9 h-9 rounded-full bg-white/70 border border-border flex items-center justify-center hover:bg-cream hover:-translate-y-0.5 transition-all">
                <InstagramLogo size={16} weight="duotone" className="text-clay" />
              </a>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-clay/20 blur-3xl"></div>
            <div className="absolute -bottom-8 -right-8 w-48 h-48 rounded-full bg-sage/30 blur-3xl"></div>
            <div className="relative rounded-3xl overflow-hidden soft-shadow-lg border border-border">
              <img src={HERO_IMG} alt="Open journal by the sea" className="w-full h-[460px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-charcoal/20 via-transparent to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6 glass rounded-2xl p-4 border border-border/50">
                <p className="text-xs tracking-[0.2em] uppercase text-sage font-bold mb-1">Day 12 · Today's prompt</p>
                <p className="font-serif text-xl leading-snug">What does 'enough' look like for you today?</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20">
        <div className="max-w-2xl mb-12">
          <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-4">Inside the journey</p>
          <h2 className="font-serif text-3xl md:text-5xl leading-tight font-medium">
            Six gentle practices, woven into one daily ritual.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <div key={f.title}
                 data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, '-')}`}
                 className="group bg-white/80 border border-border rounded-3xl p-8 soft-shadow hover:soft-shadow-lg hover:-translate-y-1 transition-all duration-300">
              <div className="w-12 h-12 rounded-2xl bg-cream flex items-center justify-center mb-5 group-hover:bg-moss/15 transition-colors">
                <f.icon size={24} weight="duotone" className="text-moss" />
              </div>
              <h3 className="font-serif text-2xl mb-2 font-medium">{f.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* STORY STRIP */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div className="rounded-3xl overflow-hidden soft-shadow-lg order-2 md:order-1">
          <img src={JOURNAL_IMG} alt="Pen on journal" className="w-full h-[440px] object-cover" />
        </div>
        <div className="order-1 md:order-2">
          <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-4">The 90-day arc</p>
          <h2 className="font-serif text-3xl md:text-5xl leading-tight font-medium mb-6">
            Three months. One steady you.
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Week by week, prompts shift from <em>noticing</em> to <em>understanding</em> to <em>committing</em>.
            Your AI guide responds to your entries, surfacing patterns you might miss.
          </p>
          <ul className="space-y-3 mt-6">
            {[
              "Week 1–4 · Notice — your patterns, your weather, your tells.",
              "Week 5–8 · Understand — what you want, what you've been carrying.",
              "Week 9–13 · Commit — the version of you that's been waiting.",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <Sparkle size={18} weight="duotone" className="text-clay mt-1 shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ABOUT ANGELA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20" id="about">
        <div className="grid grid-cols-1 md:grid-cols-[380px_1fr] gap-12 lg:gap-20 items-start">
          <div className="relative">
            <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-clay/20 blur-3xl"></div>
            <div className="absolute -bottom-8 -right-4 w-40 h-40 rounded-full bg-sage/30 blur-3xl"></div>
            <div className="relative rounded-[2rem] overflow-hidden border border-border soft-shadow-lg aspect-[4/5]">
              <img
                src="https://customer-assets.emergentagent.com/job_reflect-grow-15/artifacts/6ab7oac7_492185177_9642885715764407_5588054131521248645_n.jpg"
                alt="Angela Gehl"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-4 left-4 glass rounded-2xl px-4 py-2 border border-border/50">
              <p className="text-xs tracking-[0.2em] uppercase text-sage font-bold">Your guide</p>
              <p className="font-serif text-lg leading-tight">Angela Gehl</p>
            </div>
          </div>

          <div data-testid="about-angela">
            <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-4">About Angela</p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight font-medium mb-6">
              Award-winning intuitive. Harvard Medical School certified.
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Angela Gehl is a world-renowned intuitive and Harvard Medical School–certified
              Lifestyle and Wellness Coach (Executive Education, June 2025), with extensive training in
              neuropsychology and certifications across numerous healing modalities.
              She has been named <span className="text-charcoal font-medium">Life Coach of the Year for Saskatoon</span>
              in back-to-back wins.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              For over 15 years, Angela has worked with clients and communities — consulting with law
              enforcement agencies on missing persons cases, and guiding people through their most pivotal
              inward turns. Her work has been featured in multiple magazines and on Canadian radio.
            </p>
            <div className="grid grid-cols-2 gap-3 mt-8 mb-6">
              {[
                { k: "15+", v: "years of practice" },
                { k: "2×", v: "Coach of the Year · Saskatoon" },
                { k: "Harvard", v: "Med School certified · 2025" },
                { k: "Featured", v: "in magazines & radio" },
              ].map((s) => (
                <div key={s.v} className="rounded-2xl border border-border bg-white/70 p-4">
                  <p className="font-serif text-2xl text-moss leading-none">{s.k}</p>
                  <p className="text-xs text-muted-foreground mt-1.5">{s.v}</p>
                </div>
              ))}
            </div>
            <blockquote className="border-l-2 border-clay pl-5 italic font-serif text-lg md:text-xl text-charcoal/90">
              "The truest version of yourself isn't a destination — it's a daily, quiet decision to listen."
              <footer className="not-italic font-sans text-sm text-muted-foreground mt-2">— Angela Gehl</footer>
            </blockquote>
            <a href="https://thesmilinggoddess.fullslate.com" target="_blank" rel="noopener noreferrer"
               data-testid="landing-book-angela"
               className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full bg-clay text-white hover:bg-clay/90 transition-all">
              Book a 1:1 session with Angela <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20" id="instagram">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-4">Latest from Instagram</p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight font-medium">In her words.</h2>
          </div>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer"
             data-testid="ig-section-follow"
             className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-border bg-white hover:bg-cream transition-all text-sm">
            <InstagramLogo size={16} weight="duotone" className="text-clay" /> Follow @its.angelagehl
          </a>
        </div>
        {INSTAGRAM_POST_SHORTCODES.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {INSTAGRAM_POST_SHORTCODES.map((sc, i) => (
              <div key={sc + i} data-testid={`ig-post-${i}`}
                   className="bg-white rounded-3xl border border-border overflow-hidden soft-shadow">
                <iframe
                  src={`https://www.instagram.com/p/${sc}/embed`}
                  title={`Instagram post ${i + 1}`}
                  className="w-full"
                  style={{ height: 540, border: 0 }}
                  loading="lazy"
                  scrolling="no"
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-border p-10 text-center">
            <p className="text-muted-foreground mb-4">Latest posts will appear here once shortcodes are added.</p>
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-clay text-white hover:bg-clay/90 text-sm">
              View Angela's Instagram <InstagramLogo size={16} weight="duotone" />
            </a>
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-6 italic">
          To swap these posts, edit <code className="px-1.5 py-0.5 rounded bg-cream">INSTAGRAM_POST_SHORTCODES</code> at the top of <code className="px-1.5 py-0.5 rounded bg-cream">Landing.jsx</code> — paste the 3 most recent post shortcodes (the part after <code>/p/</code> in each post URL).
        </p>
      </section>
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20" id="pricing">
        <div className="text-center mb-12">
          <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-4">Pricing</p>
          <h2 className="font-serif text-4xl md:text-5xl font-medium">Three journeys. One quiet price each.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {[
            { id: "journey90", title: "Rediscover Yourself", sub: "90-day self-discovery", price: "$29.99", note: "7-day free trial · then lifetime", color: "bg-moss text-white hover:bg-mossDark" },
            { id: "grief180", title: "Moving through Grief", sub: "6-month grief & loss", price: "$39.99", note: "Lifetime · no trial", color: "bg-clay text-white hover:bg-clay/90" },
            { id: "sober30", title: "New Beginnings", sub: "30-day sober living", price: "$15.99", note: "Lifetime · no trial", color: "bg-sage text-white hover:bg-sage/90" },
          ].map((p) => (
            <div key={p.id} data-testid={`pricing-card-${p.id}`}
                 className="bg-white rounded-3xl border border-border p-8 soft-shadow hover:soft-shadow-lg hover:-translate-y-1 transition-all">
              <p className="text-xs tracking-[0.2em] uppercase text-sage font-bold mb-2">{p.sub}</p>
              <h3 className="font-serif text-2xl mb-4">{p.title}</h3>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="font-serif text-4xl font-medium">{p.price}</span>
                <span className="text-muted-foreground text-sm">one-time</span>
              </div>
              <p className="text-xs text-muted-foreground mb-6">{p.note}</p>
              <Link to="/register" className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full ${p.color} text-sm transition-all`}>
                Get started <ArrowRight size={14} />
              </Link>
            </div>
          ))}
        </div>
        <div className="relative bg-charcoal text-white rounded-3xl p-8 md:p-10 soft-shadow-lg overflow-hidden">
          <div className="absolute -top-12 -right-12 w-64 h-64 rounded-full bg-clay/30 blur-3xl"></div>
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-clay font-bold mb-2">Best value · bundle</p>
              <h3 className="font-serif text-3xl mb-2">All three journeys</h3>
              <p className="text-white/70">Save $25.98 — Rediscover Yourself + Grief + Sober Living, all lifetime.</p>
            </div>
            <div className="flex items-center gap-6">
              <div>
                <span className="font-serif text-5xl font-medium">$79.99</span>
              </div>
              <Link to="/register" data-testid="pricing-bundle"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-clay hover:bg-clay/90 transition-all">
                Get the bundle <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-border/60 mt-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-moss"></span>
            <span className="font-serif text-lg">Rediscover Yourself · with Angela Gehl</span>
          </div>
          <div className="flex items-center gap-5 text-xs text-muted-foreground">
            <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" aria-label="Facebook"
               data-testid="footer-facebook" className="hover:text-charcoal transition-colors">
              <FacebookLogo size={18} weight="duotone" />
            </a>
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Instagram"
               data-testid="footer-instagram" className="hover:text-charcoal transition-colors">
              <InstagramLogo size={18} weight="duotone" />
            </a>
            <a href="https://www.angelagehl.ca" className="hover:text-charcoal transition-colors" data-testid="footer-website">www.angelagehl.ca</a>
            <a href="https://thesmilinggoddess.fullslate.com" target="_blank" rel="noopener noreferrer" className="hover:text-charcoal transition-colors" data-testid="footer-book">Book a session</a>
            <span>© {new Date().getFullYear()}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
